import { useEffect, useId, useRef, useState, type ReactNode } from 'react'

/** Desktop sidebar and a keyboard-accessible mobile drawer. */
export function ResponsiveNavigation({ title, children, dark = false }: {
    title: string
    children: ReactNode
    dark?: boolean
}) {
    const [open, setOpen] = useState(false)
    const dialog = useRef<HTMLDialogElement>(null)
    const trigger = useRef<HTMLButtonElement>(null)
    const id = useId()
    useEffect(() => {
        if (!open) return
        const drawer = dialog.current
        drawer?.showModal()
        const previous = document.body.style.overflow
        document.body.style.overflow = 'hidden'
        const desktop = window.matchMedia('(min-width: 768px)')
        const closeOnDesktop = () => { if (desktop.matches) setOpen(false) }
        desktop.addEventListener('change', closeOnDesktop)
        return () => {
            document.body.style.overflow = previous
            desktop.removeEventListener('change', closeOnDesktop)
            drawer?.close()
            trigger.current?.focus()
        }
    }, [open])
    const theme = dark ? 'bg-slate-900 text-white border-slate-800' : 'bg-white text-neutral-900 border-neutral-200'
    return <>
        <div className="hidden md:flex md:shrink-0">{children}</div>
        <header className={`flex items-center gap-3 border-b px-4 py-2 md:hidden ${theme}`}>
            <button ref={trigger} type="button" aria-label="Open navigation" aria-expanded={open} aria-controls={id}
                onClick={() => setOpen(true)} className="min-h-11 min-w-11 rounded border border-current text-xl">☰</button>
            <span className="font-semibold text-sm">{title}</span>
        </header>
        {open && <dialog ref={dialog} id={id} aria-label={`${title} navigation`}
            onCancel={() => setOpen(false)} onClose={() => setOpen(false)}
            onClick={e => { if (e.target === e.currentTarget) setOpen(false) }}
            className={`fixed inset-y-0 left-0 m-0 h-dvh max-h-dvh w-72 max-w-[85vw] border-0 p-0 backdrop:bg-black/50 ${theme}`}>
            <button type="button" onClick={() => setOpen(false)} className="w-full min-h-11 border-b border-current px-4 text-right">Close navigation ×</button>
            <div className="[&>aside]:w-full [&>aside]:min-h-0" onClick={e => {
                if ((e.target as HTMLElement).closest('a, nav button')) setOpen(false)
            }}>{children}</div>
        </dialog>}
    </>
}
